import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES } from "@/lib/content";
import { Eyebrow, Reveal } from "./Reveal";

const SPANS = {
  regular: "md:col-span-6 lg:col-span-7 lg:row-span-2 min-h-[420px] lg:min-h-[560px]",
  deep: "md:col-span-6 lg:col-span-5 min-h-[300px]",
  move: "md:col-span-6 lg:col-span-5 min-h-[300px]",
  office: "md:col-span-6 lg:col-span-4 min-h-[340px]",
  construction: "md:col-span-6 lg:col-span-4 min-h-[340px]",
  airbnb: "md:col-span-12 lg:col-span-4 min-h-[340px]",
};

const Card = ({ s, i, onPick }) => (
  <Reveal delay={(i % 3) * 0.08} className={`${SPANS[s.id]} flex`}>
    <motion.article whileHover="hover" initial="rest" animate="rest" className="group relative flex w-full flex-col justify-end overflow-hidden rounded-[1.6rem] border border-white/10 bg-[#161F30]" data-testid={`service-card-${s.id}`}>
      <motion.img src={s.img} alt={s.title} loading="lazy" variants={{ rest: { scale: 1.04 }, hover: { scale: 1.12 } }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }} className="absolute inset-0 h-full w-full object-cover opacity-55 grayscale-[35%] transition-[opacity,filter] duration-700 group-hover:opacity-75 group-hover:grayscale-0" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/75 to-[#0B0F17]/10" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_0%,rgba(251,191,36,0.18),transparent_55%)] opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
      <span className="absolute right-5 top-4 font-display text-6xl font-extrabold text-white/10 transition-colors duration-500 group-hover:text-amber-300/40">{s.no}</span>
      <div className="relative p-6 sm:p-7">
        <span className="mb-4 inline-block rounded-full bg-[#0B0F17]/80 px-4 py-1.5 text-sm text-slate-200 backdrop-blur" data-testid={`service-price-${s.id}`}>
          from <span className="font-display text-lg font-bold text-amber-300">${s.price}</span>
        </span>
        <h3 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">{s.title}</h3>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-300 sm:text-base">{s.short}</p>
        <ul className="mt-4 flex flex-wrap gap-2">
          {s.points.map((p) => (
            <li key={p} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-200 backdrop-blur">
              <Check className="h-3 w-3 text-amber-300" /> {p}
            </li>
          ))}
        </ul>
        <button onClick={() => onPick(s.id)} data-testid={`service-book-${s.id}`} className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-amber-400 hover:text-[#0B0F17]">
          Book this <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
        </button>
      </div>
    </motion.article>
  </Reveal>
);

export const Services = ({ onPick }) => (
  <section id="services" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-36" data-testid="services-section">
    <div className="mb-14 grid gap-6 lg:grid-cols-12 lg:items-end">
      <Reveal className="lg:col-span-7">
        <Eyebrow>Services / 06</Eyebrow>
        <h2 className="mt-4 font-display text-4xl font-extrabold leading-[1] tracking-tight text-white sm:text-5xl lg:text-6xl">
          Six ways to <span className="text-amber-300">shine.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-5">
        <p className="text-base text-slate-300 md:text-lg">From the weekly reset to the after-build blitz — choose one service or combine several in a single booking. Starting prices shown; your final price depends on size & condition.</p>
      </Reveal>
    </div>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-12 lg:auto-rows-[minmax(270px,auto)] lg:gap-5">
      {SERVICES.map((s, i) => (
        <Card key={s.id} s={s} i={i} onPick={onPick} />
      ))}
    </div>
  </section>
);
